/* eslint-disable jsx-a11y/anchor-is-valid */
import React, { useState, useEffect, useContext, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import _ from 'underscore';
import { useDefaultStyles } from '@bingads-webui-react/with-default-styles';
import { ActivityContext } from '@bingads-webui-react/hoc-with-signal';
import { setClarityTag } from '@bingads-webui-universal/clarity';
import { usePageManager } from '@bingads-webui-campaign-react/xandr-video-campaign-shared-components';
import { CopilotRepositoryProvider } from '@bingads-webui/inline-copilot';
import { CampaignFetchProvider } from '@bingads-webui-campaign-react/campaign-http';
import { ServerContext } from '@bingads-webui-campaign-react/server-context';
import { useCopilotLoggers, useInitCopilotLoggers } from '@bingads-webui-campaign-react/copilot-loggers';
import { DisplayAdPanelWithCopilot } from './display-ad-panel-with-copilot';
import { Pagination } from './pagination';
import { CreativeWorkbench } from './creative-workbench';
import { getAdsStepStyles } from './styles';
import { addCreativesToDisplayAds } from '../model/utl';
import { RatioPicker } from './ratio-picker';
import { CreateAnotherAdMenu } from './create-another-ad-menu';
import { CreateAdWithAiDialog } from './create-ad-with-ai-dialog';
import { useCreateAds } from './use-create-ads';

export const DisplayAdsContainerWithCopilot = (props) => {
  const {
    aid,
    cid,
    i18n,
    perfMarker,
    onAdsValidation,
    currentImageIndex,
    ads = [],
    oDataConfig,
    disablePagination,
    permissions,
    customStyleFunction,
    useCustomStyleFunction,
    resetFields,
    isAdGridEditorMode,
    campaignId,
    isLoadingImage,
    initialAdsCount,
    hideFooter,
  } = props;

  const isEditScenario = useMemo(() => isAdGridEditorMode && ads && ads[0] && !!ads[0].Id, [ads, isAdGridEditorMode]);

  useInitCopilotLoggers({ campaignId, isEditScenario });

  const activity = useContext(ActivityContext);

  const defaultClasses = useDefaultStyles(getAdsStepStyles);

  const customClasses = useDefaultStyles(customStyleFunction);

  const serverContext = useContext(ServerContext);

  const { logEnterFlow } = useCopilotLoggers();

  const classes = useCustomStyleFunction ? customClasses : defaultClasses;

  const [DisplayAds, setDisplayAds] = useState(ads);
  const [copilotFinalUrl, setCopilotFinalUrl] = useState();
  const [isCreateAdDialogOpen, setIsCreateAdDialogOpen] = useState(false);
  const [useBrandKit, setUseBrandKit] = useState(true);

  const {
    goToNextPage,
    goToPrevPage,
    goToPage,
    getPageAdjustedIndex,
    currentPage,
    currentPageIndex,
    onAddItem,
    pages,
    onAddItemsToPage,
    removeItem,
    totalPages,
    maxItems,
  } = usePageManager(ads, initialAdsCount);

  const onAddAd = useCallback(() => {
    onAddItem();
    if (activity) {
      activity.signal(`Adding new ad panel - current total: ${currentPage.numberOfAdsInPage}`);
    }
  }, [activity, currentPage.numberOfAdsInPage, onAddItem]);

  const onImagePanelUpdate = useCallback((result, idx, uid) => {
    let itemAtIdx = DisplayAds[idx] || { image: null, FinalUrls: [], uid: null };

    if (!itemAtIdx.uid) {
      itemAtIdx = { ...result, uid, Id: itemAtIdx.Id };
    } else {
      itemAtIdx = { ...result, uid: itemAtIdx.uid, Id: itemAtIdx.Id };
    }

    const itemsBefore = DisplayAds.slice(0, idx);
    const itemsAfter = DisplayAds.slice(idx + 1, DisplayAds.length);
    setDisplayAds([...itemsBefore, { ...itemAtIdx }, ...itemsAfter]);
  }, [DisplayAds]);

  const onRemovePanel = useCallback((idx) => {
    if (activity) {
      activity.signal(`Removing ad panel at idx ${idx}`);
    }
    DisplayAds.splice(idx, 1);

    setDisplayAds([...DisplayAds]);

    removeItem();
  }, [DisplayAds, activity, removeItem]);

  const isAllValid = () => {
    let valid = true;
    // eslint-disable-next-line consistent-return
    _.each(DisplayAds, (ad) => {
      if (!ad.isValid) {
        valid = false;
      }
    });

    return valid;
  };

  const isAllDirty = () => {
    let dirty = false;
    // eslint-disable-next-line consistent-return
    _.each(DisplayAds, (ad) => {
      if (ad.isDirty) {
        dirty = true;
      }
    });

    return dirty;
  };

  // method to create ads in the wizard.
  // ads should be an array of ads with entire ad object.
  // ads should have the assets already saved to asset library and image experience object.
  // ads: [{ data: { image: {..}, FinalUrls: [..], creativeData: {..} }}]
  const onCreateAds = useCallback((creatives) => {
    const newAds = addCreativesToDisplayAds({ displayAds: DisplayAds, creatives });

    // get total numeber of ads possible
    const totalAdsExculudingLastPage = pages.length > 1 ? (pages.length - 1) * 10 : 0;

    // current number of items
    const currentItemCount = totalAdsExculudingLastPage + pages[pages.length - 1].numberOfAdsInPage;

    if (newAds.length > currentItemCount) {
      onAddItemsToPage(newAds.length - currentItemCount);
    }

    if (activity) {
      activity.signal(`[DisplayAdsCopilot] - Adding new copilot recommended ads - ${creatives.length}`);
    }
    setDisplayAds(newAds);
  }, [DisplayAds, onAddItemsToPage, pages, activity]);

  const createAds = useCreateAds({
    oDataConfig,
    permissions,
    aid,
    onCreateAds,
  });

  useEffect(() => logEnterFlow(), [logEnterFlow]);

  useEffect(() => {
    const result = {
      data: [...DisplayAds], isValid: isAllValid(), isDirty: isAllDirty(), currentPageIndex,
    };
    onAdsValidation(result);
    if (activity) {
      activity.signal(`onDisplayAdsContainerValidated - ${JSON.stringify(result)}`);
    }
    // disabling exhaustive deps since the onAdsValidation value changes on each rerender.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPageIndex, DisplayAds]);

  useEffect(() => {
    if (resetFields) {
      setDisplayAds([]);
    }
  }, [resetFields]);

  useEffect(() => {
    setClarityTag('XandrDisplayAds', 'true');
  }, []);

  useEffect(() => {
    if (!_.isEqual(ads, DisplayAds) && !isAdGridEditorMode) {
      // check if ads has any ad that has id. (update scenario)
      let shouldUpdate = false;

      _.each(ads, (ad) => {
        const displayAd = _.filter(DisplayAds, vAd => vAd.uid === ad.uid);

        if (displayAd && displayAd[0] && _.isUndefined(displayAd[0].Id) && ad.Id) {
          shouldUpdate = true;
        }

        // For reload scenario if we can't find any ad in local state, refresh state
        if (permissions.dynamic.MultiAdFormat && _.isEmpty(displayAd) && ad.Id) {
          shouldUpdate = true;
        }
      });

      if (shouldUpdate) {
        setDisplayAds(ads);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ads]);

  const onFinalURLFocusOut = (finalUrl) => {
    setCopilotFinalUrl(finalUrl);
  };
  const isNewLogo = _.get(permissions, ['dynamic', 'AdsCopilotUxImprovements']);

  const pageComponent = useMemo(() => (
    _.map(_.range(currentPage.numberOfAdsInPage), (idx) => {
      const displayAd = (DisplayAds[getPageAdjustedIndex(idx, currentPage.pageNumber)] && DisplayAds[getPageAdjustedIndex(idx, currentPage.pageNumber)].data);

      const uid = (DisplayAds[getPageAdjustedIndex(idx, currentPage.pageNumber)] && DisplayAds[getPageAdjustedIndex(idx, currentPage.pageNumber)].uid) || _.uniqueId('displayAd_');

      return (
        <div className={`image-panel-idx-${idx}`}>
          <DisplayAdPanelWithCopilot
            key={`${uid}-imagePanel`}
            i18n={i18n}
            perfMarker={perfMarker}
            cid={cid}
            aid={aid}
            campaignId={campaignId}
            permissions={permissions}
            displayAd={displayAd}
            useBrandKit={useBrandKit}
            onUpdate={result => onImagePanelUpdate(result, getPageAdjustedIndex(idx, currentPage.pageNumber), uid)}
            onRemovePanel={() => onRemovePanel(getPageAdjustedIndex(idx, currentPage.pageNumber), currentPage)}
            removeCloseButton={currentPage.numberOfAdsInPage === 1}
            keyIndex={uid}
            isSelectedPanel={currentImageIndex === idx}
            oDataConfig={oDataConfig}
            isLoadingImage={isLoadingImage}
          />
        </div>
      );
    })
  ), [DisplayAds, aid, campaignId, cid, currentImageIndex, currentPage, getPageAdjustedIndex, i18n, isLoadingImage, oDataConfig, onImagePanelUpdate, onRemovePanel, perfMarker, permissions]);

  return (
    <div className={classes.rootDisplayAdsContainer}>
      <CampaignFetchProvider>
        <CopilotRepositoryProvider
          campaignId="-1"
          finalUrl={copilotFinalUrl}
          locale={i18n.getLocale()}
          recommendationType="Display"
          serverContext={serverContext}
        >
          <CreativeWorkbench
            onFinalURLFocusOut={onFinalURLFocusOut}
            onCreateAds={createAds}
            isNewLogo={isNewLogo}
            useBrandKit={useBrandKit}
            setUseBrandKit={setUseBrandKit}
          />
          <RatioPicker
            onCreateAds={createAds}
          />
          <div>
            <h3>
              {i18n.getString(_TL_('Your ads'))}
            </h3>
            <p>
              {
                i18n.getString(_TL_('We create ads for you based on your assets. You can choose to add your own images below.'))
              }
            </p>
          </div>
          {pageComponent}
          {
            !hideFooter && (currentPage.numberOfAdsInPage < maxItems && !disablePagination && !_.isEmpty(DisplayAds)) &&
            <div style={{ marginTop: 10 }}>
              <CreateAnotherAdMenu
                onCreateFromExistingImage={onAddAd}
                onCreateWithAI={() => setIsCreateAdDialogOpen(true)}
              />
            </div>
          }
          {isCreateAdDialogOpen && (
            <CreateAdWithAiDialog
              isOpen={isCreateAdDialogOpen}
              onCreateAds={createAds}
              onDismiss={() => setIsCreateAdDialogOpen(false)}
              onFinalURLFocusOut={onFinalURLFocusOut}
            />
          )}
          <Pagination
            totalPages={totalPages}
            disablePagination={disablePagination}
            classes={classes}
            goToPrevPage={goToPrevPage}
            goToNextPage={goToNextPage}
            goToPage={goToPage}
            currentPageIndex={currentPageIndex}
          />
        </CopilotRepositoryProvider>
      </CampaignFetchProvider>
    </div>
  );
};

DisplayAdsContainerWithCopilot.propTypes = {
  aid: PropTypes.number.isRequired,
  cid: PropTypes.number.isRequired,
  campaignId: PropTypes.number.isRequired,
  i18n: PropTypes.shape({
    getString: PropTypes.func.isRequired,
  }).isRequired,
  perfMarker: PropTypes.shape({}).isRequired,
  permissions: PropTypes.shape({}).isRequired,
  onAdsValidation: PropTypes.func.isRequired,
  ads: PropTypes.shape({
    data: PropTypes.shape({}),
  }).isRequired,
  oDataConfig: PropTypes.shape({}),
  disablePagination: PropTypes.bool,
  customStyleFunction: PropTypes.func,
  useCustomStyleFunction: PropTypes.bool,
  resetFields: PropTypes.bool,
  currentImageIndex: PropTypes.number,
  isAdGridEditorMode: PropTypes.bool,
  isLoadingImage: PropTypes.bool,
  hideFooter: PropTypes.bool,
  initialAdsCount: PropTypes.number,
};

DisplayAdsContainerWithCopilot.defaultProps = {
  oDataConfig: null,
  disablePagination: false,
  customStyleFunction: _.noop,
  useCustomStyleFunction: false,
  resetFields: false,
  currentImageIndex: 0,
  isAdGridEditorMode: false,
  isLoadingImage: false,
  hideFooter: false,
  initialAdsCount: 0,
};
